import { isRef, unref } from 'vue';
import { extractPagination } from './paginationUtils.js';
import * as Types from './paginationUtils.js';
import * as UserTypes from './user.service.js';
import axios from '@/services/axios.setup.js';
import './tag.service.js';
import {
  accountGet,
  accountPost,
  accountDelete,
  accountPut,
  accountPatch
} from './prefixedUrls.js';
import { download } from '@/utils.js';

/**
 * @typedef Message
 * @property {number} id
 * @property {number} account_id
 * @property {string} uuid
 * @property {string} type
 * @property {string} macrotype
 * @property {string} delivery_status
 * @property {boolean} is_draft
 * @property {string} subject
 * @property {string} date
 * @property {string} from
 * @property {string} to
 * @property {string} cc
 * @property {string} bcc
 * @property {string} filename
 * @property {string} hash
 * @property {number} size
 * @property {boolean} has_attachments
 * @property {boolean} seen
 * @property {null} tag
 * @property {string | null} message_uid
 * @property {string} message_id
 * @property {number | null} reference_id
 * @property {number | null} sent_id
 * @property {number | null} receipt_status
 * @property {string} created_at
 * @property {string} updated_at
 * @property {string | null} deleted_at
 * @property {number | null} status_id
 * @property {number} folder_id
 * @property {string | null} external_id
 * @property {string | null} protocol_date
 * @property {Tag[]} navigation_tags
 * @property {boolean} is_protocollable
 * @property {any[]} groups
 * @property {UserTypes.User[]} users
 * @property {MessageStatus | null} status
 */

/**
 * TODO: improve parameters type for
 * attachments and navigation_tags
 *
 * @typedef MessageContainer
 * @property {Attachment[]} attachments
 * @property {string} body
 * @property {MessageHeaders} headers
 * @property {Message} message
 * @property {any[]} navigation_tags
 */

/**
 * @typedef MessageHeaders
 * @property {string} from
 * @property {string} to
 * @property {string} cc
 * @property {string} bcc
 * @property {string} reply-to
 * @property {string} subject
 * @property {string} date
 * @property {any} id
 * @property {string} message-id
 * @property {any} x-ricevuta
 * @property {any} x-riferimento-message-id
 */

/**
 * @typedef PaginatedMessages
 * @property {Types.Pagination} pagination
 * @property {Message[]} messages
 */

/**
 * @typedef Attachment
 * @property {string} filename
 * @property {number} size
 * @property {string} type
 */

/**
 * @typedef Folder
 * @property {number} id
 * @property {string} name
 * @property {string} slug
 * @property {boolean} label
 */

/**
 * @typedef AdvancedSearch
 * @property {string | null} from
 * @property {string | null} to
 * @property {string | null} subject
 * @property {string | null} dateFrom
 * @property {string | null} dateTo
 * @property {number | null} seen
 * @property {number[] | null} tags The ids of the tags to include
 * @property {boolean | null} withoutTags
 * @property {string | null} status
 * @property {boolean | null} withoutStatus
 * @property {number[] | null} users The ids of the assigned users
 * @property {boolean | null} notAssigned
 * @property {boolean | null} retained
 */

/**
 * @typedef MessageStatus
 * @property {number} account_id
 * @property {number} id
 * @property {number} code
 * @property {string} name
 * @property {string} created_at
 * @property {string} updated_at
 */

/**
 * @typedef ValidationError
 * @property {Object.<string, string[]>} errors
 * @property {string} message
 */

/**
 * This enumerator avoids the use of magic numbers when
 * checking the status of the receipts of a message
 */
export const RECEIPT_STATUS = {
  NULL: null,
  ACCEPTED: 1,
  DELIVERED: 2,
  ERROR: 3
}

/**
 * This enumerator avoids the use of forced strings
 * when checking the type of receipts from the `type` field
 */
export const RECEIPT_TYPE = {
  ACCEPTED: 'pec.accettazione',
  PRE_WARN_DELIVERY_ERROR: 'pec.preavviso-errore-consegna',
  TOOK_CHARGE: 'pec.presa-in-carico',
  NOT_ACCEPTED: 'pec.non-accettazione',
  VIRUS: 'pec.rilevazione-virus',
  NOT_DELIVERED: 'pec.errore-consegna',
  DELIVERED: 'pec.avvenuta-consegna',
}

/**
 * A way to consistently get info about receipt types,
 * like icons and labels
 */
export const RECEIPT_TYPE_DATA = {
  [RECEIPT_TYPE.ACCEPTED]: {
    ICON: 'fas fa-check',
    LABEL: 'Accettazione'
  },
  [RECEIPT_TYPE.PRE_WARN_DELIVERY_ERROR]: {
    ICON: 'fas fa-times',
    LABEL: 'Preavviso errore di consegna'
  },
  [RECEIPT_TYPE.TOOK_CHARGE]: {
    ICON: 'fas fa-box',
    LABEL: 'Preso in carica'
  },
  [RECEIPT_TYPE.NOT_ACCEPTED]: {
    ICON: 'fas fa-times',
    LABEL: 'Non accettata'
  },
  [RECEIPT_TYPE.VIRUS]: {
    ICON: 'fas fa-shield-halved',
    LABEL: 'Virus individuato'
  },
  [RECEIPT_TYPE.NOT_DELIVERED]: {
    ICON: 'fas fa-times',
    LABEL: 'Errore di consegna'
  },
  [RECEIPT_TYPE.DELIVERED]: {
    ICON: 'fas fa-check-double',
    LABEL: 'Consegnata'
  },
}

/**
 * This enumerator allows you to refer to the various
 * folder being sure of their actual names
 */
export const FOLDERS = {
  INBOX: 'INBOX',
  SENT: 'OUTBOX',
  RECEIPTS: 'RECEIPTS',
  DRAFTS: 'DRAFTS',
  TRASH: 'TRASHED'
}

/**
 * Get all messages from a folder
 * @param {string} accountId The account uuid
 * @param {string} folderName
 * @param {number} page The page to return (all results are paginated)
 * @param {string} search A simple search that applies to various parameters
 * @param {AdvancedSearch} asearch An advanced search passed as an object
 * @returns {Promise<PaginatedMessages>}
 */
export async function getFromFolder(
  accountId,
  folderName,
  page = 1,
  search = '',
  asearch = null,
  orderBy = null,
  order = null
) {
  let params = {};
  if (page != null) params['page'] = page;
  if (orderBy != null) params['orderBy'] = orderBy;
  if (order != null) params['order'] = order;
  if (search != null) params['search'] = search;
  if (asearch != null) {
    params = {
      ...params,
      ...prepareSearchParameters(asearch)
    }
  }

  let res = await accountGet(
    `folders/${folderName}/messages`,
    accountId,
    params
  );

  let pagination = extractPagination(res.data.messages);
  let messages = res.data.messages.data;

  return { pagination, messages };
}

/**
 * Get all messages related to a tag
 * @param {string} accountId The account uuid
 * @param {number} tagId
 * @returns {Promise<PaginatedMessages>}
 */
export async function getFromTag(
  accountId,
  tagId,
  page,
  search = '',
  asearch = null,
  orderBy = null,
  order = null
) {
  let params = {};
  if (page != null) params['page'] = page;
  if (orderBy != null) params['orderBy'] = orderBy;
  if (order != null) params['order'] = order;
  if (search != null) params['search'] = search;
  if (asearch != null) {
    params = {
      ...params,
      ...prepareSearchParameters(asearch)
    }
  }

  let res = await accountGet(
    `tags/${tagId}/messages`,
    accountId,
    params
  );

  let pagination = extractPagination(res.data);
  let messages = res.data.data;

  return { pagination, messages };
}

/**
 * Get a single message from uuid and its folder
 * @param {string} accountId The account uuid
 * @param {string} uuid The message uuid
 * @param {string} folder The folder name
 * @returns {Promise<MessageContainer>}
 */
export async function get(accountId, uuid) {
  const res = await accountGet(
    `messages/${uuid}`,
    accountId
  );

  return res.data;
}


/**
 * Creates a draft message
 * @param {string} accountId The account uuid
 * @param {string | null} referenceEmail An email to copy the attachments from (in case of forward)
 * @param {'reply'|'forward'|null} action
 * @returns {Promise<Message>}
 */
export async function createDraft(accountId, referenceEmail = null, action = null) {
  const res = await accountPost('messages', accountId, {
    referenceEmail,
    action
  });
  return res.data;
}

/**
 * @typedef UpdateDraftRequest
 * @param {string|null} body
 * @param {string[]|null} to
 * @param {string[]|null} cc
 * @param {string[]|null} bcc
 */

/**
 * Updates a draft
 * @param {string} accountId The account uuid
 * @param {UpdateDraftRequest} draft
 * @returns {Promise<Message | ValidationError>}
 */
export async function updateDraft(accountId, draft) {
  try {
    const res = await accountPut(`messages/${draft.uuid}`, accountId, draft);
    return res.data;
  } catch (err) {
    throw new Error(err.response?.data?.message ?? "Errore sconosciuto durante il salvataggio della bozza.");
  }
}

/**
 * Get all notifications for a specific message
 * @param {string} accountId The account uuid
 * @param {string} messageId The message uuid
 * @returns {Promise<Array<Message>>} The list of notifications
 */
export async function getNotifications(accountId, messageId) {
  const res = await accountGet(`messages/${messageId}/notifications`, accountId);
  return res.data;
}

/**
 * Set a message as read or unread (seen or not seen).
 * @param {string} accountId The account uuid
 * @param {string} messageId A list of message UUIDs
 * @param {boolean} status
 * @return {Promise<boolean>} The current "seen" status
 */
export async function setSeenStatus(accountId, messagesIds, status) {
  const res = await accountPatch(`messages/seen`, accountId, {
    ids: messagesIds,
    seen: status
  });

  return res.data;
}

/**
 * Set a protocol for the message
 * @param {string} accountId The account uuid
 * @param {string} messageId The message uuid
 */
export async function setMessageProtocol(accountId, messageId) {
  const res = await accountPost(`messages/${messageId}/protocol`, accountId);
  return res.data;
}

/**
 * Set the status of a message and return the new status
 * @param {string} accountId The account uuid
 * @param {string} messageId The message uuid
 * @param {number | null} status The status id, or null to remove the status
 * @returns {Promise<MessageStatus>} The new status
 */
export async function setMessageStatus(accountId, messageId, status) {
  let params = {};

  if (status) {
    params["status"] = status;
  }

  const res = await accountPatch(`messages/${messageId}/status`, accountId, params);

  return res.data;
}

/**
 * @param {string} accountId The account uuid
 * @param {string} messageId The message uuid
 */
export async function trashMessage(accountId, messageId) {
  const res = await accountDelete(`messages/${messageId}`, accountId);

  return res;
}

/**
 * Restores a message from the trash
 * @param {string} accountId The account uuid
 * @param {string} messageId The message uuid
 * @param {string} folderId The id of the folder into which this message should be moved. There is a `folder_id` property in the message
 */
export async function restoreMessage(accountId, messageId, folderId) {
  const res = await accountPost(`messages/${messageId}/restore`, accountId, { folderId });

  return res;
}

/**
 * Sends a message.
 * @param {string} accountId The account uuid
 * @param {string} messageId The message uuid
 */
export async function send(accountId, messageId) {
  const res = await accountPost(`messages/${messageId}/send`, accountId);
  return res;
}

/**
 * Downloads a message as a .eml file
 * @param {string} accountId The account uuid
 * @param {string} messageId The message uuid
 * @param {string} name The file name
 */
export async function downloadMessage(accountId, messageId, name = "email") {
  let res = await axios({
    url: `/accounts/${accountId}/messages/${messageId}/download`,
    method: 'GET',
    responseType: 'blob'
  });

  name = sanitizeFilename(name) + '.eml';
  let blob = new Blob([res.data])
  download(blob, name);
}

export async function getMessagesStats(startDate, endDate) {
  let res = await axios.get(`/stats/messages-report`,
    { params: { startDate, endDate } }
  );
  return res.data;
}

/**
 * Gets all the message folders from the API
 * @param {string} accountId The account uuid
 * @returns {Promise<Folder[]>}
 */
export async function getAllFolders(accountId) {
  const res = await accountGet(`folders`, accountId);

  return res.data;
}

/**
 * Detects which folder this message should go in when restored.
 * @param {Message} message
 * @returns {string}
 */
export function detectFolder(message) {
  if (Object.values(RECEIPT_TYPE).includes(message.type)) {
    return FOLDERS.RECEIPTS;
  }

  if (message.is_draft) {
    return FOLDERS.DRAFTS;
  }

  if (message.macrotype == "sent") {
    return FOLDERS.SENT;
  }

  return FOLDERS.INBOX;
}

/**
 * Check if the advanced search is empty or null
 * @param {AdvancedSearch | null} params
 * @returns {boolean}
 */
export function isAdvancedSearchEmpty(params) {
  if (!params) return true;

  let isEmpty = true;
  for (let key in params) {
    if (params[key] != null && params[key] != '') {
      isEmpty = false;
    }
  }

  return isEmpty;
}

/**
 * Filters and stringifies all advanced search parameters
 * if they're not null.
 * @param {AdvancedSearch | null} params
 * @returns {Object}
 */
function prepareSearchParameters(params) {
  return Object.fromEntries(
    Object.entries(params ?? {})
      .filter(([key, value]) => value !== null && value !== "")
      .map(([key, value]) => [key, JSON.stringify(value)])
  );
}

/**
 * Remove all special characters that are not allowed
 * in a file name
 */
function sanitizeFilename(name) {
  return name.replace(/[\\\/<>:\"\|\?\*]/g, '');
}
